import { ArrowLeft, Share2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { getMediaOwner } from "@/data/media-owners";
import { useCart } from "@/utils/cartContext";

import * as am5 from "@amcharts/amcharts5";
import * as am5hierarchy from "@amcharts/amcharts5/hierarchy";
import * as am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useEffect, useRef, useState, useMemo } from "react";
import { PROPERTY_DETAILS, GENERATE_IMAGE_ASSET_QUERY, GALLERY_IMAGE_ASSETS_QUERY } from "@/Graphql/Queries";
import { generateMockData } from "@/Graphql/MockData";
import client, { MAPBOX_TOKEN } from "@/Graphql/apolloClient";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import moment from "moment";
import { format } from "date-fns";
import logger from '@/utils/logger';

// Set Mapbox access token
mapboxgl.accessToken = MAPBOX_TOKEN;

// Map Component
function BillboardMap({ latitude, longitude, title }: { latitude?: number; longitude?: number; title: string }) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);

  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    // Use provided coordinates or default to a general location
    const lat = latitude || -25.2744; // Default to Australia center
    const lng = longitude || 133.7751;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [lng, lat],
      zoom: latitude && longitude ? 15 : 5, // Zoom in if we have exact coordinates
    });

    // Add navigation controls
    map.current.addControl(new mapboxgl.NavigationControl());

    // Add marker if we have coordinates
    if (latitude && longitude) {
      new mapboxgl.Marker({
        color: '#10b981' // Emerald color to match theme
      })
        .setLngLat([lng, lat])
        .setPopup(
          new mapboxgl.Popup({ offset: 25 })
            .setHTML(`<div style="padding: 8px;"><strong>${title}</strong></div>`)
        )
        .addTo(map.current);
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [latitude, longitude, title]);

  return <div ref={mapContainer} className="w-full h-full rounded-lg" />;
}

// AmCharts Hierarchy Component
function AmChartsHierarchy() {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;

    let root = am5.Root.new(chartRef.current);

    // Set themes
    root.setThemes([am5themes_Animated.default.new(root)]);

    // Create wrapper container
    let container = root.container.children.push(
      am5.Container.new(root, {
        width: am5.percent(100),
        height: am5.percent(100),
        layout: root.horizontalLayout
      })
    );

    // Create series
    let series = container.children.push(
      am5hierarchy.ForceDirected.new(root, {
        singleBranchOnly: false,
        downDepth: 2,
        topDepth: 1,
        initialDepth: 2,
        valueField: "value",
        categoryField: "name",
        childDataField: "children",
        idField: "name",
        linkWithField: "linkWith",
        manyBodyStrength: -25,
        centerStrength: 1.2,
        minRadius: 25,
        maxRadius: 80,
        nodePadding: 5
      })
    );

    // Generate data
    let data = {
      name: "SEGMENT",
      value: 200,
      children: [
        {
          name: "INTERESTS",
          value: 80,
          children: [
            { name: "Young families", value: 25 },
            { name: "Auto lovers", value: 22 },
            { name: "Fast food workers", value: 18 },
            { name: "Leisure mothers", value: 20 },
            { name: "Pet owners", value: 17 },
            { name: "Sports enthusiasts", value: 23 },
            { name: "Entertainment seekers", value: 19 }
          ]
        },
        {
          name: "OCCUPATION",
          value: 70,
          children: [
            { name: "Students", value: 25 },
            { name: "White collar", value: 30 },
            { name: "Income Group", value: 22 }
          ]
        },
        {
          name: "ACTIONS",
          value: 60,
          children: [
            { name: "Shopping", value: 20 },
            { name: "Commuting", value: 25 },
            { name: "Entertainment", value: 18 }
          ]
        }
      ]
    };

    series.data.setAll([data]);
    series.set("selectedDataItem", series.dataItems[0]);

    // Make stuff animate on load
    series.appear(1000, 100);

    return () => {
      root.dispose();
    };
  }, []);

  return <div ref={chartRef} style={{ width: "100%", height: "400px" }}></div>;
}


interface CampaignBillboardDetailProps {
  base_path: string;
  currencyCode: string;
}

// Helper function to get unit display
const getUnitDisplay = (unit: string | undefined) => {
  if (unit === 'Meter') return 'm';
  if (unit === 'Feet') return 'ft';
  return ''; // default
};

export default function BillboardDetail({ base_path, currencyCode }: CampaignBillboardDetailProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const companyId = import.meta.env.VITE_APP_COMPANY_ID;
  const currentPriceFromStateRaw = location.state?.currentPrice ?? 0;
  const currencyCodeFromState = location.state?.currencyCode || currencyCode;
  const priceFormatFromState = location.state?.priceFormat;
  const buyingFormatFromState = location.state?.buyingFormat;
  const billboardTypeFromState = location.state?.billboardType;
  const from = location.state?.from || null;
  const packageIdFromState = location.state?.packageId || null;
  const params = useParams();
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();
  const { addToCart, removeFromCart, isInCart: checkIsInCart, priceFormat } = useCart();
  const [activeTab, setActiveTab] = useState("location");
  const [selectedImage, setSelectedImage] = useState(0);

  // State for billboard data
  const [billboard, setBillboard] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isClassicType, setClassicType] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [billboardAssets, setBillboardAssets] = useState<any>({});

  // Get environment variables and configuration
  const countryId = localStorage.getItem('selectedCountryId') || localStorage.getItem('sessionDefaultCountryId');

  // Check URL parameters to determine where to navigate back
  const urlParams = new URLSearchParams(window.location.search);
  const fromStore = urlParams.get('from') === 'store';
  const ownerId = urlParams.get('ownerId');
  // Get billboard type from URL parameter, default to 'digital' if not provided
  const billboardType = urlParams.get('type') || billboardTypeFromState || 'digital';
  // Get price format from URL parameter, fallback to state
  const priceFormatFromURL = urlParams.get('priceFormat');
  // Get buying format from URL parameter, fallback to state, if not present derive from priceFormat
  const buyingFormatFromURL = urlParams.get('buyingFormat') || 
    buyingFormatFromState ||
    (priceFormat === "spot-based" ? 'spot' : null);

  // Helper function to get the correct price based on billboard type and format
  const getPriceByFormat = (billboardData: any): number => {
    if (!billboardData) {
      logger.log('getPriceByFormat: billboardData is null/undefined');
      return 0;
    }

    const billboardTypeNormalized = billboardData.type?.toLowerCase();
    // Priority: URL param > priceFormatFromState > priceFormat from cart context
    const isPriceFormatSpotBased = 
      buyingFormatFromURL === 'spot' ||
      priceFormat === "spot-based" || 
      priceFormatFromState === "spot-based";

    logger.log('=== PRICE CALCULATION ===');
    logger.log('Billboard Type:', billboardTypeNormalized);
    logger.log('BuyingFormat from URL:', buyingFormatFromURL);
    logger.log('PriceFormat:', priceFormat);
    logger.log('IsPriceFormatSpotBased:', isPriceFormatSpotBased);
    logger.log('Available price fields:', {
      perSpotPrice: billboardData.perSpotPrice,
      perDayPrice: billboardData.perDayPrice,
      'price.perSpotPrice': billboardData.price?.perSpotPrice,
      'price.perDayPrice': billboardData.price?.perDayPrice,
      'price.monthlyPrice': billboardData.price?.monthlyPrice,
      'price.amount': billboardData.price?.amount,
      'price.price': billboardData.price?.price,
    });

    // Digital billboards
    if (billboardTypeNormalized === 'digital') {
      if (isPriceFormatSpotBased) {
        // Spot-based pricing
        const spotPrice = (
          billboardData.perSpotPrice ||
          billboardData.price?.perSpotPrice ||
          billboardData.price?.amount ||
          billboardData.price?.price ||
          0
        );
        logger.log('Returning SPOT price:', spotPrice);
        return spotPrice;
      } else {
        // Day-based pricing
        const dayPrice = (
          billboardData.perDayPrice ||
          billboardData.price?.perDayPrice ||
          billboardData.price?.amount ||
          billboardData.price?.price ||
          0
        );
        logger.log('Returning DAY price:', dayPrice);
        return dayPrice;
      }
    }

    // Classic billboards
    if (billboardTypeNormalized === 'classic') {
      // If coming from landing page, use daily pricing
      if (priceFormatFromState === 'landing-page' || priceFormatFromState === 'landing-page-million' || priceFormatFromURL === 'landing-page' || priceFormatFromURL === 'landing-page-million') {
        const dayPrice = (
          billboardData.perDayPrice ||
          billboardData.price?.perDayPrice ||
          billboardData.price?.amount ||
          billboardData.price?.price ||
          0
        );
        logger.log('Returning DAILY price for classic (from landing page):', dayPrice);
        return dayPrice;
      }
      // Otherwise use monthly pricing
      const monthlyPrice = (
        billboardData.sellingRate?.monthly?.month1 ||
        billboardData.price?.monthlyPrice ||
        billboardData.price?.amount ||
        billboardData.price?.price ||
        0
      );
      logger.log('Returning MONTHLY price for classic:', monthlyPrice);
      return monthlyPrice;
    }

    // Fallback for other types
    const fallbackPrice = (
      billboardData.price?.amount ||
      billboardData.price?.price ||
      billboardData.price?.localCurrencies?.price ||
      0
    );
    logger.log('Returning FALLBACK price:', fallbackPrice);
    return fallbackPrice;
  };

  const currentPriceFromState = useMemo(() => {
    if (currentPriceFromStateRaw && currentPriceFromStateRaw !== 0) {
      return currentPriceFromStateRaw;
    }
    return getPriceByFormat(billboard);
  }, [currentPriceFromStateRaw, billboard, priceFormat, priceFormatFromState, buyingFormatFromURL]);

  const [config, setConfig] = useState<any[]>([]);
  const [audiencetab, setAudiencetab] = useState<string>('yes');
  const [configurationTab, setConfigurationTab] = useState<string>('yes');

  const fetchConfigData = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_APP_FOLDER_PATH}/json/configJson.json?t=${new Date().getTime()}`);
      const configData = await response.json();

      const propertypageconfig = configData?.find((page: { name: string; }) => page.name.toLowerCase().includes("inventory details"));
      if (propertypageconfig) {
        setConfig(propertypageconfig?.configurations || []);
        const audienceSection = propertypageconfig.configurations.find((config: any) => config.key === "audiences");
        if (audienceSection) {
          const audienceTabDefaultValue = audienceSection.fields.find((field: any) => field.key === "Audience Tab")?.default || 'yes';
          setAudiencetab(audienceTabDefaultValue);
        }
        const configurationSection = propertypageconfig.configurations.find((config: any) => config.key === "configurations");
        if (configurationSection) {
          const configurationTabDefaultValue = configurationSection.fields.find((field: any) => field.key === "Configuration Tab")?.default || 'yes';
          setConfigurationTab(configurationTabDefaultValue);
        }
      }
    } catch (error) {
      logger.error("Error fetching configuration:", error);
    }
  };

  useEffect(() => {
    fetchConfigData();
  }, []);


  const isFieldEnabled = (key: string, config: any[]) => {

    const fieldConfig = config.flatMap(cfg => cfg.fields || []).find(field => field.key === key);
    return fieldConfig ? fieldConfig.default : true;
  };

  const isAudienceTabDisabled = !isFieldEnabled('demographic segment', config) && !isFieldEnabled('audience segment', config);

  // Determine buying format - only for digital billboards
  const getBuyingFormat = (): string | null => {
    if (billboard?.type?.toLowerCase() === 'digital') {
      if (priceFormat === "spot-based" || priceFormatFromState === "spot-based") {
        return "spot";
      }
      return "day";
    }
    return null; // No buying format for classic billboards
  };

  // Determine back URL based on origin
  const getBackUrl = () => {
    const buyingFormat = getBuyingFormat();
    
    if (fromStore && ownerId) {
      return `/store/${ownerId}`;
    }
    logger.log("location state-->", location.state);
    const from = location.state?.from || null;
    const packageIdFromState = location.state?.packageId || null;
    logger.log("from-->", from, packageIdFromState);

    if (packageIdFromState && from === 'package') {
      const billboardType = location.state?.type || 'digital'; // Fallback to 'digital' if type is not in state
      return `${base_path}/package/${packageIdFromState}?type=${encodeURIComponent(billboardType)}&from=package&currentPrice=${currentPriceFromState}`;
    } else if (priceFormatFromState &&
      (priceFormatFromState === 'landing-page' || priceFormatFromState === 'landing-page-million')) {
      return `${base_path}/`;
    }
    else {
      return `${base_path}/explore`;
    }
  };

  // Fetch billboard assets for gallery
  const fetchBillboardAssets = async (billboardData: any) => {
    if (!billboardData?.id) return;

    // Robustly construct payload with fallbacks
    const id = billboardData.id;
    const type = billboardData.type || billboardData.inventoryType || billboardData.format || "";
    const thumbnail = billboardData.thumbnailPath || billboardData.thumbnail || billboardData.imageUrl || "";

    try {
      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        // For mock data, generate some gallery images
        const mockAssets = {
          [`_${id}`]: [
            { filePath: thumbnail || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg` },
          ]
        };
        setBillboardAssets(mockAssets);
      } else {
        const { data } = await client.query({
          query: GALLERY_IMAGE_ASSETS_QUERY,
          variables: {
            sort: "last_modified_date,desc",
            metadataBillboardId: id,
            metadataType: type,
          },
          fetchPolicy: 'network-only'
        });
        setBillboardAssets({ [`_${id}`]: data.galleryImageAssets || [] });
      }
    } catch (err) {
      logger.error('Error fetching billboard assets:', err);
      // Fallback to default images
      setBillboardAssets({
        [`_${id}`]: [
          { filePath: thumbnail || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg` }
        ]
      });
    }
  };

  // Get all billboard assets for gallery
  const getBillboardAssets = (id?: string): string[] => {
    const billboardId = id || billboard?.id;
    if (!billboardId) {
      return [`${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`];
    }
    const assets = billboardAssets[`_${billboardId}`];
    if (!assets || assets.length === 0) {
      return [`${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`];
    }
    return assets.map((asset: any) => asset.filePath);
  };

  // Fetch billboard details using GraphQL
  const fetchBillboardDetails = async () => {
    setIsLoading(true);
    setError(null);
    try {
      let billboardData;
      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        const mockResponse = await generateMockData(PROPERTY_DETAILS);
        billboardData = mockResponse.data.propertyDetails;
        logger.log("mock data-->", billboardData);

        setBillboard(billboardData);
        setClassicType(billboardData.type?.toLowerCase() === 'classic');
      } else {
        const { data } = await client.query({
          query: PROPERTY_DETAILS,
          variables: {
            id: params?.id,
            countryId: countryId,
            billboardType: billboardType,
            startDate: moment().format("YYYY-MM-DD"),
            endDate: moment().endOf('month').format("YYYY-MM-DD"),
          },
          fetchPolicy: 'network-only',
        });
        billboardData = data.propertyDetails;
      }
      // Add countryId and countryName to billboard data if not present
      const enrichedBillboardData = {
        ...billboardData,
        countryId: billboardData.countryId || countryId,
        countryName: billboardData.countryName || billboardData.country?.name || 'India'
      };

      setBillboard(enrichedBillboardData);
      setClassicType(billboardData.type?.toLowerCase() === 'classic');
      logger.log("mapped data-->", enrichedBillboardData);

      // Fetch billboard assets after setting billboard data
      await fetchBillboardAssets(billboardData);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch billboard details');
      setBillboard(null);
    } finally {
      setIsLoading(false);
    }
  };
  // Effect to fetch billboard details when component mounts or ID changes
  useEffect(() => {
    if (params?.id) {

      fetchBillboardDetails();
    }
  }, [params?.id, billboardType]);

  // Effect to log billboard detail page with countryId and countryName
  useEffect(() => {
    if (billboard) {
      const detailLog = {
        id: billboard.id,
        name: billboard.name,
        state: typeof billboard.state === 'object' ? billboard.state.name : billboard.state,
        price: currentPriceFromState,
        impressions: billboard.inventoryReports?.totalPotentialViews || billboard.monthlySummary?.totalVisitors || 0,
        uniqueVisitors: billboard.monthlySummary?.uniqueVisitors || 0,
        frequency: billboard.monthlySummary?.frequency || 0,
        type: billboard.type,
        status: billboard.availableBooking ? "available" : "sold-out",
        image: billboard.fullResImagePath || billboard.thumbnailPath || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`,
        currencyCode: billboard?.price?.localCurrencies?.code || currencyCodeFromState || currencyCode || 'N/A',
        specifications: {
          resolution: billboard.sizeResolution,
          duration: billboard.inventoryType === "Classic" ? "30 days" : "Variable"
        },
        isSpotBased: (priceFormat === "spot-based" || priceFormatFromState === "spot-based") ? true : false,
        priceFormat: priceFormat,
        countryId: billboard.countryId || countryId,
        countryName: billboard.countryName || billboard.country?.name || 'N/A'
      };
      logger.log('detail page log::', detailLog);
      logger.log('Billboard data with country info::', {
        countryId: billboard.countryId,
        countryName: billboard.countryName,
        country: billboard.country,
        fallbackCountryId: countryId
      });
    }
  }, [billboard]);

  // Gallery images array - use billboard assets for full resolution images
  const galleryImages = billboard ? [
    ...getBillboardAssets()
      .filter((assetUrl) => assetUrl && typeof assetUrl === 'string' && assetUrl.trim() !== '' && !assetUrl.includes(`${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`))
      .map((assetUrl, index) => ({
        src: assetUrl,
        alt: `Gallery image ${index + 1}`,
        isMain: false
      }))
  ] : [];



  // Check if billboard is in cart
  const actuallyInCart = billboard ? checkIsInCart(billboard.id) : false;

  const handleAddToCart = () => {
    if (!billboard) return;

    const cartItem = {
      id: billboard.id,
      name: billboard.displayName || billboard.name,
      countryId: billboard.countryId,
      countryName: billboard.countryName,
      location: billboard.stateName || billboard.inventoryAddress,
      state: billboard.state?.name || billboard.inventoryAddress,
      price: Number(currentPriceFromState) * 100,
      impressions: billboard.inventoryReports?.totalPotentialViews || billboard.monthlySummary?.totalVisitors || 0,
      uniqueVisitors: billboard.monthlySummary?.uniqueVisitors || 0,
      frequency: billboard.monthlySummary?.frequency || 0,
      size: billboard.sizeResolution,
      type: billboard.inventoryType || billboard.type || 'Classic',
      status: billboard.availableBooking ? "available" as const : "sold-out" as const,
      image: (billboard.fullResImagePath && billboard.fullResImagePath !== 'null' && billboard.fullResImagePath.trim() !== ''
        ? billboard.fullResImagePath
        : billboard.thumbnailPath && billboard.thumbnailPath !== 'null' && billboard.thumbnailPath.trim() !== ''
        ? billboard.thumbnailPath
        : billboard.imageUrl && billboard.imageUrl !== 'null' && billboard.imageUrl.trim() !== ''
        ? billboard.imageUrl
        : `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`),
      currencyCode: billboard?.price?.localCurrencies?.code || currencyCodeFromState || currencyCode || 'N/A',
      specifications: {
        resolution: billboard.sizeResolution,
        duration: billboard.inventoryType === "Classic" ? "30 days" : "Variable"
      },
      isSpotBased: (priceFormat === "spot-based" || priceFormatFromState === "spot-based") ? true : false,
      priceFormat: priceFormat
    };
    addToCart(cartItem);

    // ✅ Set dates to localStorage when adding to cart
    const today = new Date();
    
    localStorage.setItem('startDate', format(today, 'yyyy-MM-dd'));
    localStorage.setItem('endDate', format(today, 'yyyy-MM-dd'));

    toast({
      title: "Added to cart",
      description: "Billboard has been added to your cart successfully.",
    });
  };

  const handleRemoveFromCart = () => {
    if (!billboard) return;

    removeFromCart(billboard.id.toString());
    toast({
      title: "Removed from cart",
      description: "Billboard has been removed from your cart.",
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Skeleton className="h-96 w-full rounded-xl" />
            <div className="space-y-6">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-32 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !billboard) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="text-center">
            <CardContent className="py-16">
              <div className="text-center">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Billboard Not Found</h1>
                <p className="text-gray-600 mb-4">The billboard you're looking for doesn't exist.</p>
                <Button onClick={() => {
                  const backUrl = getBackUrl();
                  navigate(backUrl, {
                    state: packageIdFromState && from === 'package' ? {
                      currentPrice: currentPriceFromState,
                      from: 'package',
                      type: location.state?.type || 'digital',
                      packageId: packageIdFromState,
                    } : undefined,
                  });
                }}
                  variant="ghost">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {fromStore ? 'Back to Store' : 'Back to Billboards'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // If it's a package inventory type, redirect to package detail page
  if (billboard.inventoryType === "Package") {
    window.location.href = `/package/${billboard.id}`;
    return null;
  }

  function formatTime12Hour(time: string | number): string {
    if (typeof time === "number") {
      const hours = Math.floor(time / 3600);
      const minutes = Math.floor((time % 3600) / 60);
      return formatTime12Hour(`${hours}:${minutes.toString().padStart(2, "0")}`);
    }
    if (typeof time === "string") {
      const [hourStr, minuteStr = "00"] = time.split(":");
      let hour = parseInt(hourStr, 10);
      const minute = parseInt(minuteStr, 10);
      const ampm = hour >= 12 ? "PM" : "AM";
      hour = hour % 12 || 12;
      return `${hour}:${minute.toString().padStart(2, "0")} ${ampm}`;
    }
    return "N/A";
  }

  return (
    <div className="min-h-screen bg-gray-50">

      <div className="max-w-[100rem] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <div className="flex items-center justify-end mb-6">
          <Button
            variant="outline"
            onClick={() => {
              const backUrl = getBackUrl();
              navigate(backUrl, {
                state: packageIdFromState && from === 'package' ? {
                  currentPrice: currentPriceFromState,
                  from: 'package',
                  type: location.state?.type || 'digital',
                  packageId: packageIdFromState,
                } : undefined,
              });
            }}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {fromStore ? 'Back to Store' : 'Back'}
          </Button>
        </div>

        {/* Header Section */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div className="flex flex-col lg:flex-row lg:items-start gap-6 flex-1">
              {/* Billboard Image */}
              <div className="relative w-full lg:w-64 h-48 lg:h-32">
                <img
                  src={billboard.thumbnailPath && billboard.thumbnailPath !== "null"
                    ? billboard.thumbnailPath
                    : `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`}
                  alt={billboard.name}
                  className="w-full h-full object-cover rounded-lg"
                />
                {/* <div className="absolute top-2 left-2">
                  <Badge className={billboard.availableBooking ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                    {billboard.availableBooking ? "Available" : "Sold Out"}
                  </Badge>
                </div> */}
              </div>

              {/* Basic Info - Name and Details */}
              <div className="flex-1">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-3">
                  <div className="flex-1">
                     <h1
                    className="text-2xl font-bold text-gray-900 break-words leading-tight"
                    title={billboard.displayName || billboard.name}
                  >
                    {billboard.displayName || billboard.name}
                  </h1>
                    <p className="text-gray-600 text-sm mb-2">{billboard.referenceId}</p>

                    {/* State and District */}
                    <div className="flex items-center gap-1 text-sm">
                      {isFieldEnabled('district', config) && (
                        <p className="text-emerald-600">
                          {billboard.district.name}
                        </p>
                      )}
                      {isFieldEnabled('state', config) && isFieldEnabled('district', config) && (
                        <span className="text-emerald-600">,</span>
                      )}
                      {isFieldEnabled('state', config) && (
                        <p className="text-emerald-600">{billboard.state.name}</p>
                      )}
                    </div>
                  </div>

                  {/* Price - parallel to name on right */}
                  <div className="text-right flex-shrink-0 flex flex-col gap-2">
                    <div className="text-2xl font-bold text-emerald-600">
                      {billboard.price?.localCurrencies?.code || currencyCodeFromState}{" "}
                      {currentPriceFromState?.toLocaleString()}
                      {priceFormatFromState === 'landing-page-million' || priceFormatFromURL === 'landing-page-million'
                        ? "/Day"
                        : priceFormatFromState === 'landing-page' || priceFormatFromURL === 'landing-page'
                          ? "/Day"
                          : (billboard.type?.toLowerCase() === 'digital'
                            ? (buyingFormatFromURL === 'spot' || priceFormat === "spot-based" || priceFormatFromState === "spot-based"
                              ? " /Spot"
                              : " /Day")
                        : "/Month")}
                    </div>
                    {/* Add to Cart Button below price */}
                    {location?.state?.from !== 'package' && (
                    (() => {
                      const price = currentPriceFromState || billboard.price?.amount || billboard.price?.price || 0;
                      const isPriceZeroOrNA = !price || price === 0 || price === 'N/A';
                      const isDisabled = isPriceZeroOrNA || !billboard.active;
                      
                      return (
                        <Button
                          onClick={actuallyInCart ? handleRemoveFromCart : handleAddToCart}
                          disabled={isDisabled}
                          className={`w-full ${
                            actuallyInCart
                              ? "bg-red-600 hover:bg-red-700 text-white"
                              : isDisabled
                              ? "bg-gray-400 cursor-not-allowed text-white"
                              : "bg-emerald-600 hover:bg-emerald-700 text-white"
                          }`}
                        >
                          {isPriceZeroOrNA
                            ? 'Unavailable'
                            : !billboard.active
                            ? 'Sold Out'
                            : actuallyInCart
                            ? "Remove from Cart"
                            : 'Add to Cart'}
                        </Button>
                      );
                    })()
                    )}
                  </div>
                </div>

                {/* Specs Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 block">Size (W X H)</span>
                    <span className="font-medium">
                      {(() => {
                        const unit = getUnitDisplay(billboard.specification?.unit);
                        const sizes = [1, 2, 3, 4, 5, 6]
                          .map(num => {
                            const width = (billboard?.specification as Record<string, any>)[`panel${num}Width`];
                            const height = (billboard?.specification as Record<string, any>)[`panel${num}Height`];
                            return width && height ? `${width} ${unit} X ${height} ${unit}` : null;
                          })
                          .filter(Boolean);

                        const panelWidth = (billboard?.specification as Record<string, any>)["panelWidth"];
                        const panelHeight = (billboard?.specification as Record<string, any>)["panelHeight"];

                        return (
                          <>
                            {sizes.length > 0
                              ? sizes.map((size, idx) => <div key={idx}>{size}</div>)
                              : (panelWidth && panelHeight)
                                ? null
                                : 'N/A'}
                            {(panelWidth && panelHeight) &&
                              <div className="mt-1 font-medium">{`${panelWidth} ${unit} X ${panelHeight} ${unit}`}</div>
                            }
                          </>
                        );
                      })()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 block">
                      {billboard?.type?.toLowerCase() === 'classic' ? 'Number Of Faces' : 'Number Of Screens'}
                    </span>
                    <span className="font-medium">
                      {billboard?.type?.toLowerCase() === 'classic'
                        ? (billboard.specification?.faces ?? 'N/A')
                        : (billboard.specification?.screens ?? 'N/A')}
                    </span>
                  </div>
                  {isAuthenticated && (
                    <div>
                      <span className="text-gray-500 block">Impressions</span>
                      <span className="font-medium">{billboard?.monthlySummary?.totalVisitors?.toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col lg:items-end gap-4">
              {/* Media Owner Visit Store Button */}
              {billboard.mediaOwner && (() => {
                const mediaOwner = getMediaOwner(billboard.mediaOwner);
                if (mediaOwner) {
                  return (
                    <Link to={`/store/${mediaOwner.id}`}>
                      <Button variant="outline" className="w-full mt-2">
                        Visit {mediaOwner.name} Store
                      </Button>
                    </Link>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        </div>

        {/* Tabbed Content */}
        <div className="bg-white rounded-xl shadow-sm">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-5 bg-gray-50 p-1 rounded-t-xl">
              <TabsTrigger value="location" className="text-sm">Location</TabsTrigger>
              <TabsTrigger value="specification" className="text-sm">Specifications</TabsTrigger>
              {audiencetab === 'yes' && (
                <TabsTrigger value="audiences" className="text-sm">Audiences</TabsTrigger>
              )}
              {isFieldEnabled('asset', config) && (
                <TabsTrigger value="gallery" className="text-sm">Gallery</TabsTrigger>
              )}
              {configurationTab === "yes" && billboard.type?.toLowerCase() === "digital" && (
                <TabsTrigger value="configuration" className="text-sm">Configurations</TabsTrigger>
              )}
            </TabsList>

            <div className="p-6">
              <TabsContent value="location" className="space-y-6 mt-0">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Map Area */}
                  <div className="lg:col-span-2">
                    <div className="h-64 lg:h-80 rounded-lg overflow-hidden">
                      <BillboardMap
                        latitude={billboard.latitude}
                        longitude={billboard.longitude}
                        title={billboard.displayName || billboard.name}
                      />
                    </div>
                  </div>

                  {/* Location Details */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {isFieldEnabled('site owner', config) && (
                        <div>
                          <span className="text-gray-500 block mb-1">Media Owner</span>
                          {billboard.mediaOwnerName && (() => {
                            const mediaOwner = getMediaOwner(billboard.mediaOwnerName);
                            if (mediaOwner) {
                              return (
                                <div className="flex items-center space-x-2">
                                  <img
                                    src={mediaOwner.logo}
                                    alt={mediaOwner.name}
                                    className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                                  />
                                  <span className="font-medium text-emerald-600 hover:text-emerald-700 cursor-pointer">{mediaOwner.name}</span>
                                  {/* <Link to={`/store/${mediaOwner.id}`}>
                                    <span className="font-medium text-emerald-600 hover:text-emerald-700 cursor-pointer">{mediaOwner.name}</span>
                                  </Link> */}
                                </div>
                              );
                            }
                            return <span className="font-medium">{billboard.mediaOwnerName}</span>;
                          })() || <span className="font-medium">Not specified</span>}
                        </div>
                      )}
                      {isFieldEnabled('billboard type', config) && (
                        <div>
                          <span className="text-gray-500 block mb-1">Type</span>
                          <span className="font-medium">
                            {billboard.type?.toLowerCase() === 'digital' ? (
                              <>
                                Digital Inventory
                              </>
                            ) : billboard.type?.toLowerCase() === 'classic' ? (
                              <>
                                Classic Inventory
                              </>
                            ) : (
                              <>
                                N/A
                              </>
                            )}
                          </span>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-500 block mb-1">Latitude</span>
                        <span className="font-medium">{billboard.latitude || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 block mb-1">Longitude</span>
                        <span className="font-medium">{billboard.longitude || 'N/A'}</span>
                      </div>
                      {isFieldEnabled('site address', config) && (
                        <div>
                          <span className="text-gray-500 block mb-1">Address</span>
                          <span className="font-medium">{billboard.formattedAddress || 'N/A'}</span>
                        </div>
                      )}
                      {isFieldEnabled('board facing', config) && (
                        <div>
                          <span className="text-gray-500 block mb-1">Board Facing</span>
                          <span className="font-medium">{billboard.facingDirection || 'N/A'}</span>
                        </div>
                      )}
                      {isFieldEnabled('category', config) && (
                        <div>
                          <span className="text-gray-500 block mb-1">Category</span>
                          <span className="font-medium">{billboard.category || 'N/A'}</span>
                        </div>
                      )}
                      {/* <div>
                        <span className="text-gray-500 block mb-1">Site Address</span>
                        <span className="font-medium">{typeof billboard.state === 'object' ? billboard.state.name || billboard.state.id || 'N/A' : billboard.state || 'N/A'}</span>
                      </div> */}
                      {isFieldEnabled('location type', config) && (
                        <div className="col-span-2">
                          <span className="text-gray-500 block mb-1">Venue type</span>
                          <span className="font-medium">{billboard.venueType || 'N/A'}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="specification" className="space-y-6 mt-0">
                <div className="space-y-6">
                  {/* Video Specification */}
                  {billboard?.type?.toLowerCase() === 'digital' && (
                    isFieldEnabled('video support', config) ||
                    isFieldEnabled('image support', config) ||
                    isFieldEnabled('feature support', config) ||
                    isFieldEnabled('audio support', config) ||
                    isFieldEnabled('panel format', config)
                  ) ? (
                    <div>
                      <h3 className="text-lg font-semibold mb-4">Video specification</h3>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                        {isFieldEnabled('video support', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Video support</span>
                            <span className="font-medium">{billboard.specification.videoSupport?.join(', ') || 'N/A'}</span>
                          </div>
                        )}
                        {isFieldEnabled('image support', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Image support</span>
                            <span className="font-medium">{billboard.specification.imageSupport?.join(', ') || 'N/A'}</span>
                          </div>
                        )}
                        {isFieldEnabled('feature support', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Feature Support</span>
                            <span className="font-medium">{billboard.specification?.featureSupport?.join(', ') || 'N/A'}</span>
                          </div>
                        )}
                        {isFieldEnabled('audio support', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Audio Support</span>
                            <span className="font-medium">
                              {billboard.specification?.audioFormats?.join(', ') || 'N/A'}
                            </span>
                          </div>
                        )}
                        {isFieldEnabled('panel format', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Board Type</span>
                            <span className="font-medium">{billboard?.format || 'N/A'}</span>
                          </div>
                        )}
                      </div>
                    </div>) : null}

                  {/* Board Specification */}
                  {(
                    isFieldEnabled('number of panels', config) ||
                    isFieldEnabled('number of screens', config) ||
                    isFieldEnabled('lead time(Days)', config) ||
                    (isFieldEnabled('PlaySoftware', config) && !isClassicType) ||
                    (isFieldEnabled('resolution', config) && billboard?.type?.toLowerCase() === 'digital') ||
                    isFieldEnabled('size', config) ||
                    isFieldEnabled('language support', config) ||
                    isFieldEnabled('content approval', config) ||
                    (isFieldEnabled('total Sqft', config) && isClassicType) ||
                    isFieldEnabled('exclusion', config) ||
                    isFieldEnabled('sell as network', config)
                  ) ? (
                    <div>
                      <h3 className="text-lg font-semibold mb-4">Board specification</h3>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                        {isFieldEnabled('number of panels', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Number of panels</span>
                            <span className="font-medium">{billboard.specification.panel || 'N/A'}</span>
                          </div>
                        )}
                        {isFieldEnabled('number of screens', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">
                              {billboard.type?.toLowerCase() === 'classic' ? 'Number of Faces' : 'Number of screens'}
                            </span>
                            <span className="font-medium">
                              {billboard?.type?.toLowerCase() === 'classic'
                                ? (billboard.specification?.faces ?? 'N/A')
                                : (billboard.specification?.screens ?? 'N/A')}
                            </span>
                          </div>
                        )}
                        {isFieldEnabled('lead time(Days)', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Lead time (Days)</span>
                            <span className="font-medium">{billboard?.specification?.leadTime != null
                              ? Number(billboard?.specification?.leadTime)
                              : 'N/A'}</span>
                          </div>
                        )}
                        {isFieldEnabled('PlaySoftware', config) && !isClassicType && (
                          <div>
                            <span className="text-gray-500 block mb-1">Player Software</span>
                            <span className="font-medium">{billboard.specification?.playerSoftware || billboard.specification?.playerSoftwareModule?.name || 'N/A'}</span>
                          </div>
                        )}
                        {isFieldEnabled('resolution', config) && billboard?.type?.toLowerCase() === 'digital' && (
                          <div>
                            <span className="text-gray-500 block mb-1">Resolution (W x H)</span>
                            <span className="font-medium">
                              {[1, 2, 3, 4, 5, 6].map((num) => {
                                const width = billboard?.specification?.[`resolution${num}Width`];
                                const height = billboard?.specification?.[`resolution${num}Height`];
                                return width && height ? (
                                  <div key={`resolution${num}`}>{`${width} px X ${height} px`}</div>
                                ) : null;
                              }).filter(Boolean).length > 0 ? (
                                [1, 2, 3, 4, 5, 6].map((num) => {
                                  const width = billboard?.specification?.[`resolution${num}Width`];
                                  const height = billboard?.specification?.[`resolution${num}Height`];
                                  return width && height ? (
                                    <div key={`resolution${num}`}>{`${width} px X ${height} px`}</div>
                                  ) : null;
                                }).filter(Boolean)
                              ) : (
                                'N/A'
                              )}
                            </span>
                          </div>
                        )}
                        {isFieldEnabled('size', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Size (W x H)</span>
                            <span className="font-medium">
                              {(() => {
                                const unit = getUnitDisplay(billboard.specification?.unit);
                                return [1, 2, 3, 4, 5, 6].map((num) => {
                                  let width = (billboard?.specification as Record<string, any>)[`panel${num}Width`];
                                  let height = (billboard?.specification as Record<string, any>)[`panel${num}Height`];
                                  if ((!width || !height) && num === 1) {
                                    width = (billboard?.specification as Record<string, any>)["panelWidth"];
                                    height = (billboard?.specification as Record<string, any>)["panelHeight"];
                                  }
                                  return width && height ? (
                                    <div key={`panel${num}`}>{`${width} ${unit} X ${height} ${unit}`}</div>
                                  ) : null;
                                }).filter(Boolean).length > 0 ? (
                                  [1, 2, 3, 4, 5, 6].map((num) => {
                                    let width = (billboard?.specification as Record<string, any>)[`panel${num}Width`];
                                    let height = (billboard?.specification as Record<string, any>)[`panel${num}Height`];
                                    if ((!width || !height) && num === 1) {
                                      width = (billboard?.specification as Record<string, any>)["panelWidth"];
                                      height = (billboard?.specification as Record<string, any>)["panelHeight"];
                                    }
                                    return width && height ? (
                                      <div key={`panel${num}`}>{`${width} ${unit} X ${height} ${unit}`}</div>
                                    ) : null;
                                  }).filter(Boolean)
                                ) : (
                                  'N/A'
                                );
                              })()}
                            </span>
                          </div>
                        )}
                        {isFieldEnabled('language support', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Language support</span>
                            <span className="font-medium">Not Disclosed</span>
                          </div>
                        )}
                        {isFieldEnabled('content approval', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Content approval</span>
                            <span className="font-medium">{billboard.specification.contentApproval ? 'Yes' : 'No'}</span>
                          </div>
                        )}
                        {isFieldEnabled('total Sqft', config) && isClassicType && (
                          <div>
                            <span className="text-gray-500 block mb-1">Total SQFT</span>
                            <span className="font-medium">{Number(billboard.specification.panel1Size) || 'N/A'}</span>
                          </div>
                        )}
                        {isFieldEnabled('exclusion', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Exclusion</span>
                            <span className="font-medium">Not Disclosed</span>
                          </div>
                        )}
                        {isFieldEnabled('sell as network', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Sell As Network</span>
                            <span className="font-medium">{billboard?.specification?.sellAsNetwork}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              </TabsContent>

              <TabsContent value="audiences" className="space-y-6 mt-0">
                <div className="space-y-6">
                  {isAuthenticated ? (
                    <>
                      {/* Audience Metrics */}
                      <div className="grid grid-cols-3 gap-6 text-center">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="text-2xl font-bold text-gray-900">{billboard.viewCount?.toLocaleString()}</div>
                          <div className="text-sm text-gray-500">Impressions</div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="text-2xl font-bold text-gray-900">63,662</div>
                          <div className="text-sm text-gray-500">Reach</div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="text-2xl font-bold text-gray-900">12.34</div>
                          <div className="text-sm text-gray-500">Average frequency</div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-8">
                      <div className="flex flex-col items-center justify-center text-center space-y-4">
                        <Lock className="h-12 w-12 text-gray-400" />
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 mb-2">Login to view full audience and performance data</h3>
                          <p className="text-sm text-gray-600 mb-4">Access detailed audience metrics, demographic analysis, and performance insights.</p>
                          <Link to="/login">
                            <Button className="bg-emerald-600 hover:bg-emerald-700">
                              Login to View Data
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  )}

                  {isAuthenticated && (
                    // Audience Analysis Sub-tabs
                    <Tabs defaultValue="demographic" className="w-full">
                      <div className="flex gap-4 mb-6">
                        <TabsList className="bg-blue-50">
                          <TabsTrigger value="demographic" className="text-sm">Demographic details</TabsTrigger>
                          <TabsTrigger value="analysis" className="text-sm">Audience analysis</TabsTrigger>
                        </TabsList>
                        <div className="ml-auto">
                          <select className="text-sm border rounded px-3 py-1 bg-white">
                            <option>All</option>
                            <option>Top 10</option>
                            <option>Top 5</option>
                          </select>
                        </div>
                      </div>

                      <TabsContent value="demographic" className="mt-0">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                          {/* Age Demographics Bar Chart - FusionCharts */}
                          <div>
                            <h3 className="text-lg font-semibold mb-4">Age (in %)</h3>
                            <div className="bg-gray-100 rounded-lg p-8 text-center text-gray-500 h-[350px] flex items-center justify-center">
                              <p>Age Demographics Chart (GraphQL Implementation Completed)</p>
                            </div>
                          </div>

                          {/* Gender Pie Chart - FusionCharts */}
                          <div>
                            <h3 className="text-lg font-semibold mb-4">Gender</h3>
                            <div className="bg-gray-100 rounded-lg p-8 text-center text-gray-500 h-[350px] flex items-center justify-center">
                              <p>Gender Distribution Chart (GraphQL Implementation Completed)</p>
                            </div>
                          </div>
                        </div>
                      </TabsContent>

                      <TabsContent value="analysis" className="mt-0">
                        {/* Audience Analysis - amCharts Hierarchy Chart */}
                        <div className="h-96">
                          <AmChartsHierarchy />
                        </div>
                      </TabsContent>
                    </Tabs>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="gallery" className="space-y-6 mt-0">
                <div className="space-y-6">
                  {/* Main Image Display */}
                  <div className="rounded-lg overflow-hidden bg-gray-100">
                    {galleryImages[selectedImage] ? (
                      <img
                        src={galleryImages[selectedImage].src}
                        alt={galleryImages[selectedImage].alt}
                        className="w-full h-64 lg:h-96 object-contain"
                      />
                    ) : (
                      <img
                        src={`${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`}
                        alt="No image available"
                        className="w-full h-64 lg:h-96 object-contain"
                      />
                    )}
                  </div>

                  {/* Thumbnail Grid */}
                  <div className="grid grid-cols-6 gap-2">
                    {galleryImages
                      .filter(image => image.src && typeof image.src === 'string' && image.src.trim() !== '' && !image.src.includes('nomedia.jpg'))
                      .map((image, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedImage(index)}
                          className={`relative rounded-lg overflow-hidden bg-gray-100 aspect-video ${selectedImage === index ? 'ring-2 ring-emerald-500' : ''}`}
                        >
                          <img
                            src={image.src}
                            alt={image.alt}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="configuration" className="space-y-6 mt-0">
                <div className="space-y-6">
                  {/* Spot Configuration */}
                  {(isFieldEnabled('loop duration', config) ||
                    isFieldEnabled('spots/hour', config) ||
                    isFieldEnabled('max. Spot/Day', config) ||
                    isFieldEnabled('min. Spot/Day', config)) && (
                      <div>
                        <h3 className="text-lg font-semibold mb-4">Spot configuration</h3>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                          {isFieldEnabled('loop duration', config) && (
                            <div>
                              <span className="text-gray-500 block mb-1">Loop Duration</span>
                              <span className="font-medium">{billboard.price.loopDuration || 'N/A'} Seconds</span>
                            </div>
                          )}
                          {isFieldEnabled('spots/hour', config) && (
                            <div>
                              <span className="text-gray-500 block mb-1">Spot duration</span>
                              <span className="font-medium">{billboard.price.spotDuration || 'N/A'} Seconds</span>
                            </div>
                          )}
                          {isFieldEnabled('max. Spot/Day', config) && (
                            <div>
                              <span className="text-gray-500 block mb-1">Max. Spots Per Day</span>
                              <span className="font-medium">{billboard.price.maximumSpotDurationPerDay || 0}</span>
                            </div>
                          )}
                          {isFieldEnabled('min. Spot/Day', config) && (
                            <div>
                              <span className="text-gray-500 block mb-1">Min. Spots Per Day</span>
                              <span className="font-medium">{billboard.price.minimumSpotDurationPerDay || 0}</span>
                            </div>
                          )}
                          {/* <div>
                        <span className="text-gray-500 block mb-1">Availability</span>
                        <span className="font-medium">{billboard.nextAvailableDate ? moment(billboard.nextAvailableDate).format("DD MMM YYYY") : 'N/A'}</span>
                      </div> */}
                        </div>
                      </div>
                    )}
                  {/* Board Configuration */}
                  {(isFieldEnabled('operations hours', config) ||
                    isFieldEnabled('no.of Clients per Hour', config) ||
                    isFieldEnabled('mode of operation', config) ||
                    isFieldEnabled('programmatic enabled', config) ||
                    isFieldEnabled('timezone', config)) && (
                      <div>
                        <h3 className="text-lg font-semibold mb-4">Board configuration</h3>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                          {isFieldEnabled('operations hours', config) && (
                            <div>
                              <span className="text-gray-500 block mb-1">Operation hours</span>
                              <span className="font-medium">
                                {billboard.price.displayOnTime && billboard.price.displayOffTime
                                  ? `${formatTime12Hour(billboard.price.displayOnTime)} - ${formatTime12Hour(billboard.price.displayOffTime)}`
                                  : "N/A"}
                              </span>
                            </div>
                          )}
                          {isFieldEnabled('no.of Clients per Hour', config) && (
                            <div>
                              <span className="text-gray-500 block mb-1">Spots For Sale</span>
                              <span className="font-medium">{billboard.price.contentFrequencyPerHour || 1}</span>
                            </div>
                          )}
                          {isFieldEnabled('mode of operation', config) && (
                            <div>
                              <span className="text-gray-500 block mb-1">Mode Of Operation</span>
                              <span className="font-medium">{billboard.price.modeOfOperation || 'Loop Based'}</span>
                            </div>
                          )}
                          {isFieldEnabled('programmatic enabled', config) && (
                            <div>
                              <span className="text-gray-500 block mb-1">Programmatic Enabled</span>
                              <span className="font-medium">{billboard.price.programmaticEnabled ? 'Yes' : 'No'}</span>
                            </div>
                          )}
                          {isFieldEnabled('no.of Clients per Hour', config) && (
                            <div>
                              <span className="text-gray-500 block mb-1"> Max advertisers</span>
                              <span className="font-medium">{billboard.price.clients || 'N/A'}</span>
                            </div>
                          )}
                          {isFieldEnabled('timezone', config) && (
                            <div>
                              <span className="text-gray-500 block mb-1">Timezone</span>
                              <span className="font-medium">{billboard.timezone || 'N/A'}</span>
                            </div>
                          )}
                          {/* <div>
                        <span className="text-gray-500 block mb-1">Brightness</span>
                        <span className="font-medium">Auto adjust</span>
                      </div>
                  <div>
                        <span className="text-gray-500 block mb-1">Weather resistance</span>
                        <span className="font-medium">IP65</span>
                  </div>
                  <div>
                        <span className="text-gray-500 block mb-1">Connectivity</span>
                        <span className="font-medium">4G/Fiber</span>
                      </div> */}
                        </div>
                      </div>
                    )}
                  {/* Operation Information */}
                  {(isFieldEnabled('near by POI', config) || isFieldEnabled('available days', config)) && (
                    <div>
                      <h3 className="text-lg font-semibold mb-4">Operation information</h3>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                        {isFieldEnabled('near by POI', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Nearby POIs</span>
                            <span className="font-medium">{billboard.price.nearbyPOIs || 'Not Disclosed'}</span>
                          </div>
                        )}
                        {isFieldEnabled('available days', config) && (
                          <div>
                            <span className="text-gray-500 block mb-1">Available</span>
                            <span className="font-medium">
                              {Array.isArray(billboard?.price?.availableDays)
                                ? billboard.price.availableDays.join(', ')
                                : typeof billboard?.price?.availableDays === 'string' && billboard.price.availableDays !== ''
                                  ? billboard.price.availableDays
                                  : 'N/A'}
                            </span>
                          </div>
                        )}
                        {/* <div>
                            <span className="text-gray-500 block mb-1">Installation date</span>
                            <span className="font-medium">Jan 2023</span>
                    </div>
                    <div>
                            <span className="text-gray-500 block mb-1">Last maintenance</span>
                            <span className="font-medium">Nov 2024</span>
                    </div>
                    <div>
                            <span className="text-gray-500 block mb-1">Uptime</span>
                            <span className="font-medium">99.8%</span>
                    </div>
                    <div>
                            <span className="text-gray-500 block mb-1">Remote monitoring</span>
                            <span className="font-medium">Active</span>
                          </div> */}
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
